import { Box, Stack, Typography, Button, Divider, Chip } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { resetTatWizard } from '../../../../redux/tatWizardSlice';

export default function Stage5Done() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const uploadResults = useSelector((s) => s.tatWizard.uploadResults);
  const merchantName  = useSelector((s) => s.tatWizard.merchantName);
  const merchantOid   = useSelector((s) => s.tatWizard.merchantOid);
  const selectedSTs   = useSelector((s) => s.tatWizard.selectedSTs || []);

  const successCount = uploadResults.filter((r) => r.status === 'ok').length;
  const errorCount   = uploadResults.filter((r) => r.status === 'error').length;

  return (
    <Box sx={{ maxWidth: 560, mx: 'auto', py: 5, px: 2 }}>

      {/* Icon + heading */}
      <Stack alignItems="center" spacing={2} sx={{ mb: 3.5 }}>
        <Box
          sx={{
            width: 72,
            height: 72,
            borderRadius: '50%',
            bgcolor: (t) => alpha(t.palette.success.main, 0.12),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CheckCircleOutlineIcon sx={{ fontSize: 40, color: 'success.main' }} />
        </Box>

        <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center' }}>
          TAT Setup Complete
        </Typography>

        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', lineHeight: 1.6 }}>
          TAT configuration has been published to ProShip for{' '}
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>{merchantName || '—'}</Box>.
        </Typography>
      </Stack>

      {/* Stats */}
      <Stack
        direction="row"
        justifyContent="center"
        spacing={2}
        sx={{
          p: 2,
          borderRadius: 2,
          border: 1,
          borderColor: 'divider',
          bgcolor: (t) => alpha(t.palette.grey[500], 0.03),
          mb: 3,
        }}
      >
        <Stack alignItems="center" spacing={0.25}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'success.main', lineHeight: 1 }}>
            {successCount}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Files published</Typography>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack alignItems="center" spacing={0.25}>
          <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1 }}>
            {selectedSTs.length}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Service types</Typography>
        </Stack>

        {errorCount > 0 && (
          <>
            <Divider orientation="vertical" flexItem />
            <Stack alignItems="center" spacing={0.25}>
              <Typography variant="h5" sx={{ fontWeight: 800, color: 'error.main', lineHeight: 1 }}>
                {errorCount}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Failed</Typography>
            </Stack>
          </>
        )}
      </Stack>

      {/* Service type chips */}
      {selectedSTs.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} justifyContent="center" sx={{ mb: 3.5 }}>
          {selectedSTs.map((st) => (
            <Chip
              key={st}
              size="small"
              label={st}
              color="success"
              variant="outlined"
              sx={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10.5, fontWeight: 600, height: 22 }}
            />
          ))}
        </Stack>
      )}

      {/* Actions */}
      <Stack spacing={1.25}>
        <Button
          fullWidth
          size="large"
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate(merchantOid ? `/merchant-config/d2c-allocation/${merchantOid}` : '/merchant-config/d2c-allocation')}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 14 }}
        >
          Continue to Step 3 · Allocation
        </Button>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/merchant-config/tat-setup')}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700, fontSize: 14 }}
        >
          Back to merchant list
        </Button>
        <Button
          fullWidth
          size="large"
          variant="outlined"
          color="inherit"
          startIcon={<ReplayIcon />}
          onClick={() => dispatch(resetTatWizard())}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, fontSize: 14 }}
        >
          Set up another merchant
        </Button>
      </Stack>
    </Box>
  );
}
